import { useRef, useState, type ReactNode } from "react";
import { ArrowUpRight, ChevronRight, FileText, History, ImagePlus, Palette, Pencil, Plus, Settings, Shapes, Tag } from "lucide-react";
import { SHOP_SECTIONS, type ShopSection } from "../lib/shop";
import { outlinePageGroups } from "../lib/outlinePages";
import type { WorkspacePage } from "../api/contract";
import type { SectionSummary } from "./PreviewFrame";
import type { LibraryController } from "../hooks/useLibrary";
import { filterUnusedPhotos, groupEntriesByKind, usedIn } from "../lib/library";
import { Deleted, Item } from "./ContentView";
import { Skeleton, SkeletonGroup } from "./Skeleton";

/** Content and Brand need room, so they open in a modal rather than the drawer. Site-wide settings have their own modal, from the area switcher. */
export type ManagerView = "content" | "brand";

/** The blog's own screens, listed in the drawer while the Blog area is open. */
export type BlogSection = "posts" | "tags" | "settings";

interface LeftPanelProps {
  onOpen: (view: ManagerView) => void;
  /** Which area the drawer is showing: the Website's pages and tools, or the Blog's Posts, Tags and Settings. */
  area?: "website" | "blog" | "shop";
  shop?: { section: ShopSection; onSection: (section: ShopSection) => void; onDesignPages: () => void; onOpenShop?: () => void; onOpenSettings: () => void };
  /** Set while the site has a blog: the Website's drawer says where posts are written, and the Blog's lists its screens. */
  blog?: { counts: { posts: number; tags: number }; section: BlogSection; onSection: (section: BlogSection) => void; onOpenBlog: () => void; onDesignPages: () => void; onOpenSettings: () => void } | null;
  /** Every earlier version of the site, to preview or bring back. */
  onOpenHistory: () => void;
  pages: WorkspacePage[];
  pageTitles?: Record<string, string>;
  currentPath: string;
  sections: SectionSummary[] | null;
  selectedNodeId?: string;
  /** Change numbers by section, while Changes is open. */
  markers?: Map<string, number>;
  onPage: (path: string) => void;
  onSection: (nodeId: string) => void;
  onNewPage: () => void;
  /** Hands ordinary page renames to Stax; module list pages use their area's settings. */
  onRenamePage: (path: string) => void;
  onAddSection: () => void;
  showAddSection: boolean;
}

interface LibraryProps {
  library: LibraryController;
  onPickPhotos: (files: File[]) => void;
  photoAdd?: ReactNode;
}

function Outline({ pages, pageTitles, currentPath, sections, selectedNodeId, markers, onPage, onSection, onNewPage, onRenamePage, onAddSection, showAddSection, onOpenBlog, onOpenShop, onBlogSettings, onShopSettings }: Pick<LeftPanelProps, "pages" | "pageTitles" | "currentPath" | "sections" | "selectedNodeId" | "markers" | "onPage" | "onSection" | "onNewPage" | "onRenamePage" | "onAddSection" | "showAddSection"> & { onOpenBlog?: () => void; onOpenShop?: () => void; onBlogSettings?: () => void; onShopSettings?: () => void }) {
  const title = (page: WorkspacePage) => page.template || page.special ? page.title : pageTitles?.[page.path] ?? page.title;
  const pageRow = (page: WorkspacePage) => <button type="button" className="bw-outline-page" aria-current={page.path === currentPath ? "page" : undefined} aria-expanded={page.path === currentPath} onClick={() => onPage(page.path)}>{(page.template || page.special) && <FileText size={16} aria-hidden="true" />}<span className="bw-outline-label">{title(page)}</span></button>;
  const pageSections = (page: WorkspacePage, area: "blog" | "shop" | null = null) => {
    if (page.path !== currentPath) return null;
    const settings = area === "blog" ? onBlogSettings : area === "shop" ? onShopSettings : undefined;
    return <div className="bw-outline-section-group">
      <p className="bw-outline-section-heading">Sections</p>
      <ul className="bw-outline-sections" aria-label={`${title(page)} sections`}>
        {(sections ?? []).map((section) => <li key={section.nodeId}><button type="button" data-hidden={section.hidden || undefined} aria-current={section.nodeId === selectedNodeId ? "true" : undefined} onClick={() => onSection(section.nodeId)}><span className="bw-outline-label">{section.label}</span>{markers?.has(section.nodeId) && <span className="bw-outline-edited">Edited <span className="bw-change-number" data-placed>{markers.get(section.nodeId)}</span></span>}{section.hidden && <span className="bw-outline-badge">Hidden</span>}</button></li>)}
        {showAddSection && sections && sections.length > 0 && <li><button type="button" className="bw-outline-add" onClick={onAddSection}><Plus size={16} aria-hidden="true" />Add section</button></li>}
        {settings && <li><button type="button" className="bw-outline-page-settings" onClick={settings}><Settings size={16} aria-hidden="true" />{area === "blog" ? "Blog settings" : "Shop settings"}<ArrowUpRight size={14} aria-hidden="true" /></button></li>}
        {!area && page.path !== "/" && !page.template && !page.special && <li><button type="button" className="bw-outline-rename" onClick={() => onRenamePage(page.path)}><Pencil size={16} aria-hidden="true" />Rename…</button></li>}
        {sections?.length === 0 && <li className="bw-outline-empty">Sections appear here when this page loads.</li>}
      </ul>
    </div>;
  };
  return <div className="bw-left-content">
    <div className="bw-left-group-head"><h3>Pages</h3><button type="button" className="bw-btn bw-btn--text bw-left-new" onClick={onNewPage}><Plus size={16} aria-hidden="true" /> New page</button></div>
    <ul className="bw-outline-pages">
      {outlinePageGroups(pages).map(({ page, templates, area }) => {
        // The list and templates are one group; opening the list adds sections below all its pages.
        const link = area === "blog" && onOpenBlog ? { label: "Write posts", onOpen: onOpenBlog } : area === "shop" && onOpenShop ? { label: "Manage products", onOpen: onOpenShop } : null;
        return <li key={page.id} data-template={page.template ? "" : undefined} data-special={page.special} data-area-link={link ? "" : undefined}>
          {link && <button type="button" className="bw-btn bw-btn--text bw-outline-area-link" onClick={link.onOpen}>{link.label}<ChevronRight size={14} aria-hidden="true" /></button>}
          {pageRow(page)}
          {templates.length > 0 && <ul className="bw-outline-templates" aria-label={`${title(page)} templates`}>
            {templates.map(template => <li key={template.id} data-template>{pageRow(template)}{pageSections(template)}</li>)}
          </ul>}
          {pageSections(page, area)}
        </li>;
      })}
    </ul>
  </div>;
}

/** The Blog area's screens, with how many there are of each, and the way to the pages that are designed in the Website. */
function BlogNav({ counts, section, onSection, onDesignPages }: NonNullable<LeftPanelProps["blog"]>) {
  const items: Array<{ id: BlogSection; label: string; count?: number; icon: ReactNode }> = [
    { id: "posts", label: "Posts", count: counts.posts, icon: <FileText size={20} aria-hidden="true" /> },
    { id: "tags", label: "Tags", count: counts.tags, icon: <Tag size={20} aria-hidden="true" /> },
    { id: "settings", label: "Settings", icon: <Settings size={20} aria-hidden="true" /> },
  ];
  return <div className="bw-left-content bw-blog-nav">
    <ul className="bw-menu-group" aria-label="Blog">
      {items.map((item) => <li key={item.id}><button type="button" className="bw-menu-item" aria-current={section === item.id ? "page" : undefined} onClick={() => onSection(item.id)}>{item.icon}<span>{item.label}</span>{item.count !== undefined && <small>{item.count}</small>}</button></li>)}
    </ul>
    <button type="button" className="bw-blog-design-link" onClick={onDesignPages}><span><strong>Design blog pages</strong><small>In Website</small></span><ArrowUpRight size={16} aria-hidden="true" /></button>
  </div>;
}

type LibraryTab = "photos" | "collections";

/** Photos, and collections with deleted content, as two tabs in the Content modal. */
export function Library({ library, onPickPhotos, photoAdd }: LibraryProps) {
  const [tab, setTab] = useState<LibraryTab>("photos");
  const [unusedOnly, setUnusedOnly] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  if (!library.library) return library.failed ? <div className="bw-unavailable" role="status"><strong>Your library couldn’t be loaded</strong><button type="button" className="bw-btn bw-btn--quiet" onClick={library.retry}>Try again</button></div> : <SkeletonGroup label="Loading your library" className="bw-left-content">{[90, 75, 65].map((width) => <Skeleton key={width} width={`${width}%`} height={44} radius={8} />)}</SkeletonGroup>;
  const { entries, deleted } = library.library;
  const photos = filterUnusedPhotos(entries, unusedOnly);
  const unusedCount = filterUnusedPhotos(entries, true).length;
  const groups = groupEntriesByKind(entries);
  const orders = new Map<string, string[]>();
  entries.forEach((entry) => {
    if (!entry.list) return;
    const order = orders.get(entry.list.collectionId) ?? Array(entry.list.count).fill("");
    order[entry.list.index] = entry.id;
    orders.set(entry.list.collectionId, order);
  });
  // Photos being described stay in view until they're added.
  const shown: LibraryTab = photoAdd ? "photos" : tab;
  const counts: Record<LibraryTab, number> = {
    photos: entries.filter((entry) => entry.kind === "media").length,
    collections: groups.reduce((total, group) => total + group.entries.length, 0),
  };
  const tabs: LibraryTab[] = ["photos", "collections"];
  return <div className="bw-left-content bw-left-library">
    <div className="bw-library-tabs" role="tablist" aria-label="Content" onKeyDown={(event) => {
      if (photoAdd || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
      event.preventDefault();
      const next = shown === "photos" ? "collections" : "photos";
      setTab(next);
      requestAnimationFrame(() => document.getElementById(`bw-library-tab-${next}`)?.focus());
    }}>
      {tabs.map((item) => (
        <button key={item} id={`bw-library-tab-${item}`} type="button" role="tab" aria-selected={shown === item} aria-controls={`bw-library-view-${item}`} tabIndex={shown === item ? 0 : -1} disabled={Boolean(photoAdd) && item !== "photos"} onClick={() => setTab(item)}>
          {item === "photos" ? "Photos" : "Collections"} <span>{counts[item]}</span>
        </button>
      ))}
    </div>
    {shown === "photos" ? (
      <section id="bw-library-view-photos" role="tabpanel" aria-labelledby="bw-library-tab-photos">
        <input ref={picker} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif" multiple hidden onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ""; if (files.length) onPickPhotos(files); }} />
        {photoAdd ?? <>
          <div className="bw-library-toolbar">
            <div className="bw-photo-filters" role="group" aria-label="Filter photos"><button type="button" aria-pressed={!unusedOnly} onClick={() => setUnusedOnly(false)}>All</button><button type="button" aria-pressed={unusedOnly} onClick={() => setUnusedOnly(true)}>Unused <span>{unusedCount}</span></button></div>
            <button type="button" className="bw-btn bw-btn--quiet bw-left-upload" onClick={() => picker.current?.click()}><ImagePlus size={16} aria-hidden="true" /> Add photos</button>
          </div>
          {photos.length ? <ul className="bw-left-photos">{photos.map((entry) => <li key={entry.id} className="bw-left-photo"><Item entry={entry} library={library} /><span className="bw-left-used">{usedIn(entry).join(", ") || "Unused"}</span></li>)}</ul> : <p className="bw-activity-empty">No photos match this filter.</p>}
        </>}
      </section>
    ) : (
      <section id="bw-library-view-collections" role="tabpanel" aria-labelledby="bw-library-tab-collections">
        {groups.map((group) => <div key={group.kind} className="bw-left-collection"><h4>{group.label} <span>{group.entries.length}</span></h4><ul className="bw-items">{group.entries.map((entry) => <Item key={entry.id} entry={entry} library={library} listOrder={entry.list ? orders.get(entry.list.collectionId) : undefined} />)}</ul></div>)}
        {deleted.length > 0 && <div className="bw-left-collection"><h4>Deleted · can be restored</h4><ul className="bw-items">{deleted.map((item) => <Deleted key={item.id} item={item} library={library} />)}</ul></div>}
      </section>
    )}
  </div>;
}


/** The drawer's site management: the page outline in place, then Content and Brand (modals) and History (the Changes panel). */
export function LeftPanel({ onOpen, onOpenHistory, area = "website", blog, shop, ...outline }: LeftPanelProps) {
  if (area === "shop" && shop) return <div className="bw-left-panel"><nav className="bw-left-content bw-blog-nav" aria-label="Shop"><ul className="bw-menu-group">{SHOP_SECTIONS.map((item) => <li key={item}><button type="button" className="bw-menu-item" aria-current={shop.section === item ? "page" : undefined} onClick={() => shop.onSection(item)}>{item[0]!.toUpperCase() + item.slice(1)}</button></li>)}</ul><button type="button" className="bw-blog-design-link" onClick={shop.onDesignPages}><span><strong>Design shop pages</strong><small>In Website</small></span><ArrowUpRight size={16} /></button></nav></div>;
  if (area === "blog" && blog) return <div className="bw-left-panel"><BlogNav {...blog} /></div>;
  return <div className="bw-left-panel">
    <Outline {...outline} {...(blog ? { onOpenBlog: blog.onOpenBlog, onBlogSettings: blog.onOpenSettings } : {})} {...(shop?.onOpenShop ? { onOpenShop: shop.onOpenShop, onShopSettings: shop.onOpenSettings } : {})} />
    <ul className="bw-menu-group bw-manage-links" aria-label="Manage your website">
      <li>
        <button type="button" className="bw-menu-item" aria-haspopup="dialog" onClick={() => onOpen("content")}>
          <Shapes size={20} aria-hidden="true" />
          <span>Content</span>
          <small>Photos and text</small>
          <ChevronRight className="bw-menu-chevron" size={16} aria-hidden="true" />
        </button>
      </li>
      <li>
        <button type="button" className="bw-menu-item" aria-haspopup="dialog" onClick={() => onOpen("brand")}>
          <Palette size={20} aria-hidden="true" />
          <span>Brand</span>
          <small>Colours and type</small>
          <ChevronRight className="bw-menu-chevron" size={16} aria-hidden="true" />
        </button>
      </li>
      <li>
        <button type="button" className="bw-menu-item" onClick={onOpenHistory}>
          <History size={20} aria-hidden="true" />
          <span>History</span>
          <small>Go back in time</small>
          <ChevronRight className="bw-menu-chevron" size={16} aria-hidden="true" />
        </button>
      </li>
    </ul>
  </div>;
}
